import { type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '../../utils';

// TrustBadge twin (@agion/ui/next; design-of-record
// components/governance/TrustBadge, handover-06 mission-details READ-FIRST
// acceptance 3): the trust tier chip. TrustTiers is a four-tier ordinal
// (0 Restricted, 1 Limited, 2 Standard, 3 Elevated). A tier outside that range
// WARNS once and renders an explicit neutral unknown state ("Tier 5?", or
// "Tier ?" when the value is not a number) instead of falling back to
// Standard: in a governance surface the ceiling is the meaning, and a silent
// fallback renders a plausible, wrong rule. Fills are built from tokens that
// are redefined for dark, so no tier turns into a glaring block there.

export type TrustTier = 0 | 1 | 2 | 3;

export interface TrustBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Trust tier: 0 Restricted, 1 Limited, 2 Standard, 3 Elevated. */
  tier: TrustTier;
  /** @default "md" */
  size?: 'sm' | 'md' | 'lg';
  /** Show the tier label text. @default true */
  showLabel?: boolean;
  /** Show the tier icon (tiers 2–3 only). @default true */
  showIcon?: boolean;
  /** Override the label text. */
  label?: string;
}

interface TierLook { bg: string; fg: string; bd: string; label: string }

// The main entry exports this component too (agion/Agion#3405), so it also
// paints on the current theme. Each part reads --agn-trust-<tier>-<part>
// first: only the current theme's dark block declares them (globals.css),
// because that theme keeps the -50 washes light in .dark. Everywhere else the
// record tokens apply; the -800 fallbacks are the record's light values, as
// the current theme declares no -800 step for error or success.
const trustVar = (tier: TrustTier, part: 'bg' | 'fg' | 'bd', record: string) => `var(--agn-trust-${tier}-${part}, ${record})`;

const TIERS: Record<TrustTier, TierLook> = {
  0: { bg: trustVar(0, 'bg', 'var(--color-error-50)'), fg: trustVar(0, 'fg', 'var(--color-error-800, #9f1239)'), bd: trustVar(0, 'bd', 'color-mix(in srgb, var(--color-error-600) 35%, transparent)'), label: 'Restricted' },
  1: { bg: trustVar(1, 'bg', 'var(--color-warning-50)'), fg: trustVar(1, 'fg', 'var(--color-brand-800)'), bd: trustVar(1, 'bd', 'color-mix(in srgb, var(--color-warning-600) 40%, transparent)'), label: 'Limited' },
  2: { bg: trustVar(2, 'bg', 'var(--color-success-50)'), fg: trustVar(2, 'fg', 'var(--color-success-700)'), bd: trustVar(2, 'bd', 'color-mix(in srgb, var(--color-success-600) 40%, transparent)'), label: 'Standard' },
  // Elevated is a deeper green than Standard: an ordinal step, carried by the
  // fill as well as the shield-check.
  3: { bg: trustVar(3, 'bg', 'color-mix(in srgb, var(--color-success-700) 22%, transparent)'), fg: trustVar(3, 'fg', 'var(--color-success-800, #065f46)'), bd: trustVar(3, 'bd', 'color-mix(in srgb, var(--color-success-700) 45%, transparent)'), label: 'Elevated' },
};

const SIZES = {
  sm: { h: 'var(--inline-h, 24px)', p: '0 9px', i: 14 },
  md: { h: 'var(--inline-h, 24px)', p: '0 10px', i: 14 },
  lg: { h: 'var(--inline-h-lg, 28px)', p: '0 12px', i: 16 },
} as const;

export function isTrustTier(tier: unknown): tier is TrustTier {
  return tier === 0 || tier === 1 || tier === 2 || tier === 3;
}

/** The unknown-state word: "Tier 5?" for an out-of-range number, "Tier ?" otherwise. */
export function unknownTierLabel(tier: unknown): string {
  return typeof tier === 'number' && Number.isFinite(tier) ? `Tier ${tier}?` : 'Tier ?';
}

export function getTrustTierLabel(tier: TrustTier): string {
  return isTrustTier(tier) ? TIERS[tier].label : unknownTierLabel(tier);
}

const warnedTiers = new Set<unknown>();

/** Warns once per distinct invalid value. Exported for tests. */
export function warnUnknownTier(tier: unknown): void {
  if (isTrustTier(tier) || warnedTiers.has(tier)) return;
  warnedTiers.add(tier);
  console.warn(
    `[TrustBadge] tier ${String(tier)} is out of range. TrustTiers is 0-3 (Restricted/Limited/Standard/Elevated). ` +
      'Rendering an explicit unknown state rather than defaulting to Standard, which would read as a valid ceiling.',
  );
}

/** Test hook: forget which invalid tiers already warned. */
export function resetTrustTierWarnings(): void {
  warnedTiers.clear();
}

export interface TierGlyphProps {
  tier: number;
  size?: number;
  style?: CSSProperties;
}

/** The bare tier shield, coloured by `currentColor`; tier 3 adds the check. */
export function TierGlyph({ tier, size = 14, style }: TierGlyphProps) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none', ...style }}
    >
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      {tier === 3 && <path d="m9 12 2 2 4-4" />}
    </svg>
  );
}

export function TrustBadge({ tier, size = 'md', showLabel = true, showIcon = true, label, className, style, ...props }: TrustBadgeProps) {
  const known = isTrustTier(tier);
  if (!known) warnUnknownTier(tier);
  const look: TierLook = known
    ? TIERS[tier]
    : { bg: 'var(--muted)', fg: 'var(--text-muted)', bd: 'var(--border)', label: unknownTierLabel(tier) };
  const s = SIZES[size] ?? SIZES.md;
  return (
    <span
      data-trust-tier={known ? tier : 'unknown'}
      className={cn('inline-flex flex-none items-center justify-center whitespace-nowrap', className)}
      style={{
        gap: 5,
        height: s.h,
        padding: s.p,
        boxSizing: 'border-box',
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        fontSize: 'min(1em, var(--text-sm, 0.875rem))',
        lineHeight: 1,
        background: look.bg,
        color: look.fg,
        border: `1px solid ${look.bd}`,
        borderRadius: 'var(--radius-md)',
        ...style,
      }}
      {...props}
    >
      {showIcon && known && tier >= 2 && <TierGlyph tier={tier} size={s.i} />}
      {showLabel && <span>{label || look.label}</span>}
    </span>
  );
}
